import { MultiselectOption, SiteInfo } from '@cbo/shared-library';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import Skeleton from '@mui/material/Skeleton';
import { useSites } from '../../../contexts/siteContext';
import Multiselect from '../Multiselect/Multiselect';
import SingleSelect from '../SingleSelect/SingleSelect';
import { SingleSelectGroup } from '../types';
import { useSiteSynchronization } from './siteFilterHooks';

export type SiteFilterProps = {
  /**
   * @param controllerName The name of the controller for the multiselect
   * @param singleSelect Whether the site filter should be single select
   * @param isTextField Optional, whether to render as a text field
   */
  controllerName: string;
  singleSelect?: boolean;
  includeOrganizationEvents?: boolean;
  defaultChecked?: boolean;
  useGlobalSiteDefault?: boolean;
  /**
   * @param updateGlobalSiteOnChange When true, updates both local and global site contexts when site selection changes
   */
  updateGlobalSiteOnChange?: boolean;
  isTextField?: boolean;
  textFieldLabel?: string;
  isRequired?: boolean;
};

/**
 * Common site filter component. Populates site options from the
 * Sites Context, which shows accessible sites for a user per org.
 * Defaults to previously selected sites, or the currently
 * selected site from the Site Selector.
 */
function SiteFilter({
  controllerName,
  singleSelect,
  includeOrganizationEvents,
  defaultChecked,
  useGlobalSiteDefault,
  updateGlobalSiteOnChange,
  isTextField,
  textFieldLabel,
  isRequired,
}: Readonly<SiteFilterProps>) {
  const { t } = useTranslation();
  const { selectedSite, updateIsSiteFiltering, sortedSites } = useSites();
  const formContext = useFormContext();
  const watchedSiteValue = useWatch({
    control: formContext.control,
    name: controllerName,
  });
  const allSites: MultiselectOption[] = useMemo(() => {
    const siteOptions = sortedSites
      .filter((option): option is Omit<SiteInfo, 'name'> & { name: string } => option.name !== undefined)
      .map<MultiselectOption>((option) => ({
        label: option.name,
        subtext: option.referenceId ?? '',
        id: option.enterpriseUnitId,
      }));

    return includeOrganizationEvents
      ? [
          {
            label: t('admin.auditActivityLogging.organizationEventsOption'),
            id: 'organizationEvents',
          },
          ...siteOptions,
        ]
      : siteOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedSites]);

  const singleSelectOptions: SingleSelectGroup[] = useMemo(() => {
    const siteOptions = sortedSites
      .filter((option) => option.name !== undefined)
      .map<SingleSelectGroup>((option) => ({
        label: option.name ?? '',
        subtext: option.referenceId ?? '',
        id: option.enterpriseUnitId,
      }));

    return includeOrganizationEvents
      ? [
          {
            label: t('admin.auditActivityLogging.organizationEventsOption'),
            id: 'organizationEvents',
          },
          ...siteOptions,
        ]
      : siteOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedSites]);

  useEffect(() => {
    // This component being mounted indicates whether
    // or not the site selector should be disabled
    updateIsSiteFiltering(true);
    return () => {
      updateIsSiteFiltering(false);
    };
  }, [updateIsSiteFiltering]);

  // Handle all site synchronization logic
  useSiteSynchronization(updateGlobalSiteOnChange, watchedSiteValue);

  const defaultValue: MultiselectOption = useGlobalSiteDefault
    ? {
        label: selectedSite.name ?? '',
        subtext: selectedSite.referenceId ?? '',
        id: selectedSite.enterpriseUnitId,
      }
    : {
        label: '',
        subtext: '',
        id: '',
      };

  const singleDefaultValue: SingleSelectGroup = useGlobalSiteDefault
    ? {
        label: selectedSite.name ?? '',
        subtext: selectedSite.referenceId ?? '',
        id: selectedSite.enterpriseUnitId,
      }
    : {
        label: '',
        subtext: '',
        id: '',
      };

  if (useGlobalSiteDefault && !selectedSite.name?.length) {
    return (
      <Skeleton variant='rectangular' height={32} width={104} sx={{ borderRadius: '8px' }} data-testid='Skeleton' />
    );
  }

  // Determine currentSite value for multiselect based on configuration
  const getCurrentSiteValue = (): MultiselectOption[] | undefined => {
    if (!useGlobalSiteDefault) {
      return undefined;
    }
    if (defaultChecked) {
      return undefined;
    }
    return [defaultValue];
  };

  const multiselectCurrentSite = getCurrentSiteValue();

  const singleSelectProps = {
    controllerName,
    buttonText: t('globalFilters.site'),
    titleText: t('globalFilters.site'),
    options: singleSelectOptions,
    ...(!isTextField && { currentSite: singleDefaultValue }),
    ...(isTextField && { isTextField: true }),
    ...(isTextField && { textFieldLabel }),
    isRequired,
  };

  const multiselectProps = {
    controllerName,
    singular: t('globalFilters.site'),
    plural: t('globalFilters.sites'),
    options: allSites,
    titleText: t('globalFilters.site'),
    ...(!isTextField && {
      currentSite: multiselectCurrentSite,
    }),
    defaultChecked,
    ...(isTextField && { isTextField: true }),
    ...(isTextField && { textFieldLabel }),
    isRequired,
  };

  if (singleSelect) {
    return <SingleSelect {...singleSelectProps} />;
  }

  return <Multiselect {...multiselectProps} />;
}

SiteFilter.defaultProps = {
  includeOrganizationEvents: false,
  singleSelect: false,
  defaultChecked: false,
  useGlobalSiteDefault: true,
  updateGlobalSiteOnChange: false,
  isTextField: false,
  textFieldLabel: '',
  isRequired: false,
};

export default SiteFilter;
